import { useCallback, type FunctionComponent, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { DynamicButton } from '@trello/dynamic-tokens';
import { Button } from '@trello/nachos/button';
import { CloseIcon } from '@trello/nachos/icons/close';
import type { GAModalTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import GAModalImagePngSrc from './beta-ga-modal-image.png';

import * as styles from './GAAnnouncementModalWithoutOptIn.module.less';

export const GAAnnouncementModalWithoutOptIn: FunctionComponent<{
  onClose: () => void;
}> = ({ onClose }) => {
  const intl = useIntl();

  const onClickClose = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'closeGAModalButton',
      source: 'gaAnnouncementModal',
    });
    onClose();
  }, [onClose]);

  const onClickDismiss = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'closeGAAnnouncementModalButton',
      source: 'gaAnnouncementModal',
    });
    onClose();
  }, [onClose]);

  return (
    <div className={styles.gaAnnouncementModal}>
      <img className={styles.headingImage} src={GAModalImagePngSrc} alt="" />
      <DynamicButton
        className={styles.closeButton}
        iconBefore={<CloseIcon size="medium" />}
        onClick={onClickClose}
        aria-label={intl.formatMessage({
          id: 'templates.dialog_close_button.close-dialog-label',
          defaultMessage: 'Close dialog',
          description: 'Aria label for the close dialog button',
        })}
        data-testid={getTestId<GAModalTestIds>('ga-modal-close-button')}
      />
      <div className={styles.contentContainer}>
        <FormattedMessage
          id="templates.beta_ga.trello-betas-wrapping-up"
          defaultMessage="Trello Beta's wrapping up soon"
          description="GA modal content"
          tagName="h1"
        />
        <p>
          <FormattedMessage
            id="templates.beta_ga.starting-in-july-all-the-new-features"
            defaultMessage="Starting in July, all the new features you experienced in Beta will be released to everyone."
            description="GA modal content"
            tagName="span"
          />
          &nbsp;
          <FormattedMessage
            id="templates.beta_ga.after-beta-scheduling-in-planner"
            defaultMessage="After Beta, scheduling in Planner and AI for Quick Capture will be available only in our <strong>Standard</strong> and <strong>Premium</strong> plans."
            description="GA modal content"
            tagName="strong"
            values={{
              strong: (chunks: ReactNode) => <span>{chunks}</span>,
            }}
          />
        </p>
        <FormattedMessage
          id="templates.beta_ga.we-appreciate-your-feedback"
          defaultMessage="We appreciate your feedback — keep it coming!"
          description="GA modal content"
          tagName="p"
        />
        <div className={styles.ctasContainer}>
          <Button onClick={onClickDismiss}>
            <FormattedMessage
              id="templates.beta_ga.opted-in-ga-modal-close-button"
              defaultMessage="Close"
              description="GA modal dismiss button"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
